import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { X, Sparkles, Copy, Check, Loader } from 'lucide-react';
import { templatesAPI, getErrorMessage } from '@/services/api';
import type { PostTemplate } from '@/types/templates';

type RemixType = 'tone' | 'length' | 'platform';
type ToneOption = 'casual' | 'professional' | 'viral';
type LengthOption = 'shorter' | 'same' | 'longer';
type PlatformOption = 'twitter' | 'instagram' | 'linkedin' | 'tiktok' | 'facebook';

interface RemixResult {
  label: string;
  content: string;
  type: RemixType;
  value: ToneOption | LengthOption | PlatformOption;
}

interface TemplateRemixPanelProps {
  isOpen: boolean;
  onClose: () => void;
  template: PostTemplate | null;
  onUseVariation: (content: string) => void;
}

export const TemplateRemixPanel: React.FC<TemplateRemixPanelProps> = ({
  isOpen,
  onClose,
  template,
  onUseVariation,
}) => {
  const { t } = useTranslation(['templates', 'common']);
  const [remixType, setRemixType] = useState<RemixType>('tone');
  const [selectedTones, setSelectedTones] = useState<ToneOption[]>([]);
  const [selectedLengths, setSelectedLengths] = useState<LengthOption[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<PlatformOption[]>([]);
  const [results, setResults] = useState<RemixResult[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const toneOptions: ToneOption[] = ['casual', 'professional', 'viral'];
  const lengthOptions: LengthOption[] = ['shorter', 'same', 'longer'];
  const platformOptions: PlatformOption[] = [
    'twitter',
    'instagram',
    'linkedin',
    'tiktok',
    'facebook',
  ];

  const remixMutation = useMutation({
    mutationFn: async () => {
      if (!template) throw new Error('No template selected');

      const selectedOptions = {
        tone: remixType === 'tone' ? selectedTones : undefined,
        length: remixType === 'length' ? selectedLengths : undefined,
        platform: remixType === 'platform' ? selectedPlatforms : undefined,
      };

      const response = await templatesAPI.aiRemix(template.id, {
        type: remixType,
        ...selectedOptions,
      });

      return response.data;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (data: any) => {
      const formattedResults: RemixResult[] = [];

      if (remixType === 'tone' && data.variations?.tone) {
        formattedResults.push(
          ...data.variations.tone.map(
            (
              item: { tone: ToneOption; content: string },
              _idx: number
            ) => ({
              label: t(`templates:remix.tone.${item.tone}`),
              content: item.content,
              type: 'tone' as const,
              value: item.tone,
            })
          )
        );
      } else if (remixType === 'length' && data.variations?.length) {
        formattedResults.push(
          ...data.variations.length.map(
            (
              item: { length: LengthOption; content: string },
              _idx: number
            ) => ({
              label: t(`templates:remix.length.${item.length}`),
              content: item.content,
              type: 'length' as const,
              value: item.length,
            })
          )
        );
      } else if (remixType === 'platform' && data.variations?.platform) {
        formattedResults.push(
          ...data.variations.platform.map(
            (
              item: { platform: PlatformOption; content: string },
              _idx: number
            ) => ({
              label: t(`templates:remix.platform.${item.platform}`),
              content: item.content,
              type: 'platform' as const,
              value: item.platform,
            })
          )
        );
      }

      setResults(formattedResults);
      toast.success(t('templates:remix.generated'));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('common:errors.remix_failed')));
    },
  });

  const toggleTone = (tone: ToneOption) => {
    setSelectedTones((prev) =>
      prev.includes(tone) ? prev.filter((t) => t !== tone) : [...prev, tone]
    );
  };

  const toggleLength = (length: LengthOption) => {
    setSelectedLengths((prev) =>
      prev.includes(length)
        ? prev.filter((l) => l !== length)
        : [...prev, length]
    );
  };

  const togglePlatform = (platform: PlatformOption) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  const handleGenerate = () => {
    const hasSelection =
      (remixType === 'tone' && selectedTones.length > 0) ||
      (remixType === 'length' && selectedLengths.length > 0) ||
      (remixType === 'platform' && selectedPlatforms.length > 0);

    if (!hasSelection) {
      toast.error(t('templates:remix.select_option'));
      return;
    }

    remixMutation.mutate();
  };

  const copyToClipboard = (content: string, resultId: string) => {
    navigator.clipboard.writeText(content);
    setCopiedId(resultId);
    toast.success(t('common:copied'));
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative w-full max-w-2xl rounded-lg bg-n-card border border-n-border shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-n-border px-6 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-500" />
            <h2 className="text-n-text font-semibold">
              {t('templates:remix.title')} &ldquo;{template.name}&rdquo;
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-n-hover rounded transition-colors"
            aria-label={t('common:close')}
          >
            <X className="h-5 w-5 text-n-text" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
          <p className="text-n-text-md text-sm mb-6">
            {t('templates:remix.description')}
          </p>

          {/* Remix Type Selector */}
          <div className="mb-6">
            <label className="block text-n-text font-semibold text-sm mb-3">
              {t('templates:remix.type')}
            </label>
            <div className="flex gap-2">
              {(['tone', 'length', 'platform'] as RemixType[]).map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    setRemixType(type);
                    setResults([]);
                    setSelectedTones([]);
                    setSelectedLengths([]);
                    setSelectedPlatforms([]);
                  }}
                  className={`px-4 py-2 rounded text-xs font-semibold transition-colors ${
                    remixType === type
                      ? 'text-white'
                      : 'text-n-text-md bg-n-hover border border-n-border'
                  }`}
                  style={
                    remixType === type ? { backgroundColor: '#2383e2' } : undefined
                  }
                >
                  {t(`templates:remix.${type}`)}
                  {remixType === type && ' ✓'}
                </button>
              ))}
            </div>
          </div>

          {/* Options Panel */}
          <div className="mb-6 p-4 bg-n-hover rounded border border-n-border">
            <label className="block text-n-text font-semibold text-sm mb-3">
              {remixType === 'tone'
                ? t('templates:remix.select_tones')
                : remixType === 'length'
                  ? t('templates:remix.select_lengths')
                  : t('templates:remix.select_platforms')}
            </label>

            <div className="flex flex-wrap gap-2">
              {remixType === 'tone' &&
                toneOptions.map((tone) => (
                  <button
                    key={tone}
                    onClick={() => toggleTone(tone)}
                    className={`px-3 py-2 rounded text-xs font-semibold transition-colors ${
                      selectedTones.includes(tone)
                        ? 'text-white'
                        : 'text-n-text-md bg-n-card border border-n-border hover:bg-n-hover'
                    }`}
                    style={
                      selectedTones.includes(tone)
                        ? { backgroundColor: '#2383e2' }
                        : undefined
                    }
                  >
                    {t(`templates:remix.tone.${tone}`)}
                  </button>
                ))}

              {remixType === 'length' &&
                lengthOptions.map((length) => (
                  <button
                    key={length}
                    onClick={() => toggleLength(length)}
                    className={`px-3 py-2 rounded text-xs font-semibold transition-colors ${
                      selectedLengths.includes(length)
                        ? 'text-white'
                        : 'text-n-text-md bg-n-card border border-n-border hover:bg-n-hover'
                    }`}
                    style={
                      selectedLengths.includes(length)
                        ? { backgroundColor: '#2383e2' }
                        : undefined
                    }
                  >
                    {t(`templates:remix.length.${length}`)}
                  </button>
                ))}

              {remixType === 'platform' &&
                platformOptions.map((platform) => (
                  <button
                    key={platform}
                    onClick={() => togglePlatform(platform)}
                    className={`px-3 py-2 rounded text-xs font-semibold transition-colors ${
                      selectedPlatforms.includes(platform)
                        ? 'text-white'
                        : 'text-n-text-md bg-n-card border border-n-border hover:bg-n-hover'
                    }`}
                    style={
                      selectedPlatforms.includes(platform)
                        ? { backgroundColor: '#2383e2' }
                        : undefined
                    }
                  >
                    {t(`templates:remix.platform.${platform}`)}
                  </button>
                ))}
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={remixMutation.isPending}
            className="w-full mb-6 py-2 rounded font-semibold text-xs text-white transition-opacity disabled:opacity-50"
            style={{ backgroundColor: '#2383e2' }}
          >
            {remixMutation.isPending ? (
              <span className="flex items-center justify-center gap-2">
                <Loader className="h-4 w-4 animate-spin" />
                {t('templates:remix.generating')}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Sparkles className="h-4 w-4" />
                {t('templates:remix.generate')}
              </span>
            )}
          </button>

          {/* Results */}
          {results.length > 0 && (
            <div>
              <h3 className="text-n-text font-semibold text-sm mb-3">
                {t('templates:remix.results')}
              </h3>
              <div className="space-y-3">
                {results.map((result, idx) => {
                  const resultId = `${result.type}-${result.value}-${idx}`;
                  const isCopied = copiedId === resultId;

                  return (
                    <div
                      key={resultId}
                      className="p-4 bg-n-card border border-n-border rounded"
                    >
                      <p className="text-n-text-lt text-[10px] font-semibold mb-2">
                        {result.label}
                      </p>
                      <p className="text-n-text text-sm mb-3 line-clamp-3">
                        {result.content}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => onUseVariation(result.content)}
                          className="flex-1 px-3 py-2 rounded text-xs font-semibold text-white transition-opacity hover:opacity-90"
                          style={{ backgroundColor: '#2383e2' }}
                        >
                          {t('templates:remix.use_this')}
                        </button>
                        <button
                          onClick={() => copyToClipboard(result.content, resultId)}
                          className="px-3 py-2 rounded text-xs font-semibold text-n-text bg-n-hover border border-n-border hover:bg-n-card transition-colors"
                        >
                          {isCopied ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-n-border px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded text-xs font-semibold text-n-text bg-n-hover border border-n-border hover:bg-n-card transition-colors"
          >
            {t('common:close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateRemixPanel;
